import { useState, useEffect } from 'react';

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL;
const STRAPI_TOKEN = process.env.REACT_APP_STRAPI_API_TOKEN;
const CACHE_KEY = 'l2it_whats_new_cache';

const MONTHS = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
];

export function getMonthSlug() {
  const d = new Date();
  return `${MONTHS[d.getMonth()]}-${d.getFullYear()}-product-update`;
}

function readCache() {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY)); }
  catch { return null; }
}

function writeCache(slug, data, published) {
  localStorage.setItem(CACHE_KEY, JSON.stringify({ slug, data, published, checkedAt: Date.now() }));
}

function stripHtml(html) {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent ?? '';
}

function resolveImageUrl(media) {
  if (!media) return null;
 
  const attrs = media?.data?.attributes ?? media?.attributes ?? media;
  if (!attrs) return null;
  const url = attrs.formats?.medium?.url ?? attrs.formats?.small?.url ?? attrs.url ?? null;
  if (!url) return null;
  return url.startsWith('http') ? url : `${STRAPI_URL}${url}`;
}

function parsePost(raw, slug) {
  const post = raw.attributes ?? raw;

  const sections = Array.isArray(post.updateSections)
    ? [...post.updateSections].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    : [];

  const items = sections.map(s => ({
    type: s.updateType === 'bug_fix' ? 'fix'
      : s.updateType === 'feature' ? 'feature'
        : 'improvement',
    title: s.title,
    description: stripHtml(s.description),
    screenshotUrl: resolveImageUrl(s.screenshot),
  }));

  return {
    title: post.title,
    blogUrl: `https://www.getlisten2it.com/blog/${slug}`,
    items,
  };
}

async function fetchFromStrapi(slug, signal) {
  if (!STRAPI_URL || !STRAPI_TOKEN) {
    console.error('[WhatsNew] REACT_APP_STRAPI_URL or REACT_APP_STRAPI_API_TOKEN is not set');
    return null;
  }
  const url = `${STRAPI_URL}/api/blogs?filters[slug][$eq]=${slug}`
    + `&populate[updateSections][populate][0]=screenshot`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${STRAPI_TOKEN}` },
    signal,
  });
  if (!res.ok) throw new Error(`Strapi ${res.status}`);
  const json = await res.json();
  const post = json?.data?.[0];
  if (!post) return null;
  return parsePost(post, slug);
}

const RECHECK_INTERVAL = 4 * 60 * 60 * 1000; // re-check every 4h while blog not yet published

function shouldFetch() {
  const cached = readCache();
  const slug   = getMonthSlug();
  const now    = Date.now();

  if (!cached || cached.slug !== slug) return true; // new month or cold start

  if (cached.published) return false; // blog is live and cached for this month

  // Blog wasn't published last check — retry only after RECHECK_INTERVAL
  return (now - (cached.checkedAt ?? 0)) >= RECHECK_INTERVAL;
}

export function useWhatsNew() {
  const [data, setData] = useState(() => readCache()?.data ?? null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let controller;

    async function run() {
      if (!shouldFetch()) return;

      controller = new AbortController();
      const slug = getMonthSlug();
      setLoading(true);
      setError(false);

      try {
        const fresh = await fetchFromStrapi(slug, controller.signal);
        if (controller.signal.aborted) return;
        if (fresh) {
          writeCache(slug, fresh, true);
          setData(fresh);
        } else {
          // Blog not published yet — preserve last known good data, record the check time
          const prevData = readCache()?.data ?? null;
          writeCache(slug, prevData, false);
          if (prevData) setData(prevData);
        }
      } catch {
        if (controller.signal.aborted) return;
        const fallback = readCache()?.data;
        if (fallback) setData(fallback);
        else setError(true);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    run();

    const onVisible = () => { if (document.visibilityState === 'visible') run(); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      controller?.abort();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { data, loading, error };
}
